<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { listCustomOptionPresets, type CustomOptionGroup, type CustomOptionInputType, type CustomOptionPreset } from "../api/products";
import CustomParameterEditor from "../components/CustomParameterEditor.vue";
import { cloneGroups, type ParameterKind } from "../components/custom-parameters/model";
import { addLibraryGroup, appendBlankLibraryGroup, libraryGroups } from "../components/custom-parameters/library-draft";
import { saveLibraryDraft, sharedLibrary } from "../components/custom-parameters/library-binding";
import { mapPresets } from "../components/custom-parameters/binding";

const props = defineProps<{ initialPresets?: CustomOptionPreset[] }>();
const busy = ref(false), blocked = ref(false), readingImages = ref(false);
const previewOnly = computed(() => props.initialPresets !== undefined);
const disabled = computed(() => loading.value || !!loadError.value || busy.value || blocked.value);
const readOnlyCodes = computed(() => groups.value.filter(sharedLibrary).map(group => group.code));
const groups = ref<CustomOptionGroup[]>([]), baseline = ref<CustomOptionGroup[]>([]), kinds = ref<Record<string, ParameterKind>>({});
const images = ref<{ groupId: string; valueId: string; file: File }[]>([]);
const name = ref(""), newKind = ref<ParameterKind | "multi-select">("text"), message = ref(""), loading = ref(true), loadError = ref(""), revision = ref(0), issues = ref(0);
const editing = ref<{ id: string; name: string; inputType: CustomOptionInputType; unit: string; required: boolean; kind: ParameterKind | "auto" } | null>(null);
const settingsPanel = ref<HTMLElement | null>(null);
let returnFocus: HTMLElement | null = null;
const dirty = computed(() => JSON.stringify(groups.value) !== JSON.stringify(baseline.value) || images.value.length > 0);
const archived = computed(() => groups.value.filter(group => !group.isActive));
const count = computed(() => groups.value.reduce((sum, group) => sum + group.values.length, 0));
async function load() {
  loading.value = true; loadError.value = "";
  try {
    const mapped = mapPresets(props.initialPresets ?? await listCustomOptionPresets());
    issues.value = mapped.issues.length; baseline.value = libraryGroups(mapped.presets); groups.value = cloneGroups(baseline.value); images.value = []; kinds.value = {}; blocked.value = false; revision.value++;
  } catch { loadError.value = "公共参数读取失败，请重新加载页面。"; }
  finally { loading.value = false; }
}
function addGroup() {
  if (disabled.value) return;
  try {
    const next = addLibraryGroup(groups.value, name.value, ["quantity", "number-input"].includes(newKind.value) ? "NUMBER" : ["size", "text-input"].includes(newKind.value) ? "TEXT" : newKind.value === "multi-select" ? "MULTI_SELECT" : "SINGLE_SELECT");
    const group = next.at(-1)!; groups.value = next; kinds.value[group.id] = newKind.value === "multi-select" ? "text" : newKind.value; name.value = ""; message.value = `已在本页添加“${group.name}”，尚未保存到公共库`;
  } catch (error) { message.value = error instanceof Error ? error.message : "添加失败"; }
}
async function appendGroup() {
  if (disabled.value) return;
  groups.value = appendBlankLibraryGroup(groups.value);
  message.value = "已追加空白大类，请填写名称；修改尚未保存";
  await nextTick();
  const inputs = document.querySelectorAll<HTMLInputElement>(".library-page .parameter-name-input");
  const input = inputs[inputs.length - 1];
  input?.focus(); input?.scrollIntoView({ behavior: "smooth", block: "center" });
}
function toggleGroup(group: CustomOptionGroup) {
  if (disabled.value || sharedLibrary(group)) return;
  groups.value = groups.value.map(item => item.id === group.id ? { ...item, isActive: !item.isActive } : item);
  message.value = `“${group.name}”已在草稿中${group.isActive ? '停用' : '启用'}`;
}
function deleteGroup(group: CustomOptionGroup) {
  if (disabled.value || sharedLibrary(group)) return;
  if (baseline.value.some(item => item.id === group.id)) {
    groups.value = groups.value.map(item => item.id === group.id ? { ...item, isActive: false } : item);
    message.value = `“${group.name}”已移入已删除参数，保存后生效；已有商品保持不变`;
  } else {
    groups.value = groups.value.filter(item => item.id !== group.id);
    delete kinds.value[group.id];
    message.value = "已移除未保存的大类";
  }
  images.value = images.value.filter(image => image.groupId !== group.id);
  if (editing.value?.id === group.id) closeSettings();
}
async function openSettings(group: CustomOptionGroup, event: Event) {
  if (disabled.value || sharedLibrary(group)) return;
  returnFocus = event.currentTarget as HTMLElement;
  editing.value = { id: group.id, name: group.name, inputType: group.inputType, unit: group.unit ?? "", required: group.required, kind: kinds.value[group.id] ?? "auto" };
  await nextTick(); settingsPanel.value?.querySelector<HTMLInputElement>("input")?.focus();
}
function closeSettings() { editing.value = null; returnFocus?.focus(); }
function trapSettingsFocus(event: KeyboardEvent) {
  const controls = settingsPanel.value?.querySelectorAll<HTMLElement>('input,select,button:not(:disabled)');
  if (!controls?.length) return;
  const first = controls[0], last = controls[controls.length - 1];
  if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
  else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
}
function saveSettings() {
  const edit = editing.value;
  if (!edit) return;
  const label = edit.name.normalize("NFKC").trim();
  if (!label || label.length > 100 || groups.value.some(group => group.id !== edit.id && group.name.normalize("NFKC").trim() === label)) { message.value = "名称不能为空、超过100字或与其他大类重复"; return; }
  groups.value = groups.value.map(group => group.id === edit.id ? { ...group, name: label, inputType: edit.inputType, unit: edit.unit.trim() || null, required: edit.required } : group);
  if (edit.kind === "auto") delete kinds.value[edit.id]; else kinds.value[edit.id] = edit.kind;
  closeSettings(); message.value = "大类设置已更新到本页草稿";
}
function discard() {
  if (dirty.value && !window.confirm("放弃本页公共参数草稿及本地图片预览？")) return;
  groups.value = cloneGroups(baseline.value); kinds.value = {}; images.value = []; editing.value = null; revision.value++; message.value = "已恢复读取时的公共参数";
}
async function save() {
  if (previewOnly.value || disabled.value || issues.value || readingImages.value || !dirty.value) return;
  busy.value = true; message.value = "正在保存，请勿关闭页面…";
  try {
    const presets = await saveLibraryDraft(baseline.value, groups.value, images.value);
    const previousKinds = new Map(groups.value.map(group => [group.code, kinds.value[group.id]]));
    baseline.value = libraryGroups(presets); groups.value = cloneGroups(baseline.value);
    kinds.value = Object.fromEntries(groups.value.flatMap(group => previousKinds.get(group.code) ? [[group.id, previousKinds.get(group.code)!]] : []));
    images.value = []; revision.value++; message.value = "公共参数已保存并重新读取核对。普通商品副本保持独立。";
  } catch (error) {
    blocked.value = true;
    message.value = `${error instanceof Error ? error.message : "保存未完成"}。已停止后续写入；可能已有部分操作生效，请点击重新读取核对，勿重复提交。`;
  } finally { busy.value = false; }
}
async function reload() {
  if (busy.value || (dirty.value && !window.confirm("重新读取服务器实际数据？当前未保存草稿将放弃。"))) return;
  await load(); message.value = loadError.value ? "读取失败，请稍后重新读取" : "已读取服务器当前数据，请核对后再编辑";
}
function beforeUnload(event: BeforeUnloadEvent) { if (dirty.value || busy.value) { event.preventDefault(); event.returnValue = ""; } }
onBeforeRouteLeave(() => !busy.value && (!dirty.value || window.confirm("公共参数草稿尚未保存，确定离开？")));
onMounted(() => { void load(); window.addEventListener("beforeunload", beforeUnload); });
onBeforeUnmount(() => window.removeEventListener("beforeunload", beforeUnload));
</script>

<template>
  <main class="library-page">
    <header class="library-heading">
      <div><h1>常用定制参数库</h1><p>统一维护公共选项与示意图片，供新商品选用。</p></div><span>{{ groups.length }} 大类 · {{ count }} 选项</span>
    </header>
    <div
      class="preview-notice"
      role="note"
    >
      <strong>{{ previewOnly ? "界面预览" : "公共参数库" }}</strong><span>{{ previewOnly ? "本页修改和图片仅保留在当前草稿，尚未接入保存。" : "保存后供新商品选用；已有普通商品保持独立。共享贴纸库在本页只读。" }}</span>
    </div>
    <p
      v-if="issues"
      role="alert"
    >
      {{ issues }} 条异常记录已隔离显示，原数据未修改。
    </p>
    <p
      v-if="loadError"
      role="alert"
    >
      {{ loadError }}
    </p>
    <form
      class="library-create"
      @submit.prevent="addGroup"
    >
      <label><span class="sr-only">新增大类名称</span><input
        v-model="name"
        aria-label="新增大类名称"
        maxlength="100"
        placeholder="新增大类，例如：特殊内托"
        :disabled="disabled"
      ></label>
      <label><span class="sr-only">新大类展示方式</span><select
        v-model="newKind"
        aria-label="新大类展示方式"
      ><option value="text">单选（文字）</option><option value="image">单选（图文）</option><option value="multi-select">多选</option><option value="number-input">数字输入</option><option value="text-input">文字输入</option><option value="quantity">数量阶梯</option><option value="size">常用尺寸</option></select></label>
      <button
        class="primary"
        type="submit"
        :disabled="disabled || !name.trim()"
      >
        ＋ 新增大类
      </button>
    </form>
    <p
      v-if="loading"
      role="status"
    >
      正在读取公共参数…
    </p>
    <CustomParameterEditor
      v-else-if="!loadError"
      :key="revision"
      v-model="groups"
      rows-only
      editable-group-names
      :kinds="kinds"
      :disabled="disabled"
      :read-only-codes="readOnlyCodes"
      persistent-images
      @image-reading="readingImages = $event"
      @image-draft="images = $event"
    >
      <template #group-actions="{ group }">
        <div
          v-if="!sharedLibrary(group)"
          class="library-row-tools"
        >
          <button
            type="button"
            :aria-label="'设置' + (group.name || '未命名大类')"
            :disabled="disabled || !group.name.trim()"
            @click="openSettings(group, $event)"
          >
            设置
          </button>
          <button
            type="button"
            :aria-label="'删除' + (group.name || '未命名大类')"
            :disabled="disabled"
            @click="deleteGroup(group)"
          >
            删除
          </button>
        </div>
      </template>
    </CustomParameterEditor>
    <details
      v-if="archived.length"
      class="library-archive"
    >
      <summary>已删除参数 {{ archived.length }} 项（可恢复）</summary>
      <div
        v-for="group in archived"
        :key="group.id"
        class="library-archive-row"
      >
        <span>{{ group.name }}</span>
        <button
          type="button"
          :aria-label="'恢复' + group.name"
          :disabled="disabled || sharedLibrary(group)"
          @click="toggleGroup(group)"
        >
          恢复
        </button>
      </div>
    </details>
    <button
      class="library-add-category"
      type="button"
      :disabled="disabled"
      @click="appendGroup"
    >
      ＋ 新增公共参数大类
    </button>
    <p
      v-if="message"
      class="library-message"
      role="status"
    >
      {{ message }}
    </p>
    <footer class="library-footer">
      <button
        class="primary"
        type="button"
        :disabled="previewOnly || disabled || !!issues || readingImages || !dirty"
        @click="save"
      >
        {{ busy ? "正在保存…" : previewOnly ? "保存公共参数（待接入）" : "保存公共参数" }}
      </button><button
        type="button"
        :disabled="!dirty || busy || blocked"
        @click="discard"
      >
        放弃修改
      </button><button
        type="button"
        :disabled="busy"
        @click="reload"
      >
        重新读取
      </button><span>{{ dirty ? '有未保存的草稿' : '尚未修改' }} · 本地图片 {{ images.length }} 张</span>
    </footer>
    <div
      v-if="editing"
      class="settings-backdrop"
      @keydown.esc.stop="closeSettings"
      @keydown.tab="trapSettingsFocus"
    >
      <section
        ref="settingsPanel"
        class="library-settings"
        role="dialog"
        aria-modal="true"
        aria-labelledby="library-settings-title"
      >
        <form @submit.prevent="saveSettings">
          <h2 id="library-settings-title">
            大类设置
          </h2>
          <label>名称<input
            v-model="editing.name"
            maxlength="100"
            required
          ></label>
          <label>填写方式<select
            v-model="editing.inputType"
            aria-label="填写方式"
          ><option value="SINGLE_SELECT">单选</option><option value="MULTI_SELECT">多选</option><option value="NUMBER">数字输入</option><option value="TEXT">文字输入</option></select></label>
          <label v-if="editing.inputType === 'SINGLE_SELECT' || editing.inputType === 'MULTI_SELECT'">展示方式<select v-model="editing.kind"><option value="auto">自动识别</option><option value="text">文字选项</option><option value="image">图文选项</option><option value="quantity">数量阶梯</option><option value="size">常用尺寸</option></select></label>
          <label>单位<input
            v-model="editing.unit"
            maxlength="20"
            placeholder="可选"
          ></label>
          <label class="required-toggle"><input
            v-model="editing.required"
            type="checkbox"
          >客户端必填</label>
          <small>原有数字范围、尺寸等配置保持原样。展示方式仅用于本次编辑，不写入数据库；刷新后按原有名称与图片自动识别。</small>
          <p
            v-if="message"
            role="status"
          >
            {{ message }}
          </p><div class="settings-actions">
            <button
              type="button"
              @click="closeSettings"
            >
              取消
            </button><button
              class="primary"
              type="submit"
            >
              应用到草稿
            </button>
          </div>
        </form>
      </section>
    </div>
  </main>
</template>

<style scoped>
.library-page { max-width:1280px; margin:0 auto; padding:24px 20px 40px; background:#fff; color:#333; font:14px/1.5 system-ui,"Microsoft YaHei",sans-serif; color-scheme:light; }
.library-page * { box-sizing:border-box; }
.library-heading { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:16px; }
.library-heading h1 { margin:0; font-size:24px; color:#000; }
.library-heading p { margin:4px 0 0; color:#666; font-size:13px; }
.library-heading>span { font-size:12px; color:#666; white-space:nowrap; }
.preview-notice { display:flex; gap:12px; flex-wrap:wrap; padding:10px 12px; background:#f5f5f5; border:1px solid #e5e5e5; font-size:12px; }
.library-create { display:flex; flex-wrap:wrap; gap:8px; padding:16px 0; }
.library-create>label:first-child { flex:1; min-width:180px; }
.library-page input,.library-page select,.library-page button { font:inherit; color:#333; border:1px solid #e5e5e5; border-radius:4px; padding:7px 10px; background:#fff; }
.library-create input { width:100%; }
.library-page button { cursor:pointer; }
.library-page button:hover:not(:disabled) { border-color:#000; background:#f5f5f5; }
.library-page .primary { background:#000; color:#fff; border-color:#000; }
.library-page .primary:hover:not(:disabled) { background:#333; }
.library-page button:disabled { color:#666; background:#f5f5f5; border-color:#e5e5e5; cursor:not-allowed; }
.library-page :focus-visible { outline:2px solid #000; outline-offset:3px; }
.library-page :deep(.parameter-row) { grid-template-columns:200px minmax(0,1fr); }
.library-page :deep(.parameter-label) { width:200px; }
.library-page :deep(.parameter-name-block) { padding:6px 8px; }
.library-row-tools { position:absolute; top:50%; right:0; transform:translateY(-50%); display:flex; gap:4px; }
.library-row-tools button { min-width:38px; padding:4px; font-size:12px; }
.library-archive { margin-top:12px; padding:10px 12px; border:1px solid #e5e5e5; color:#666; }
.library-archive summary { cursor:pointer; }
.library-archive-row { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-top:8px; }
.library-page .library-add-category { display:block; width:100%; margin:12px 0 16px; min-height:36px; border:1px dashed #e5e5e5; background:transparent; color:#333; text-align:center; }
.library-page .library-add-category:hover:not(:disabled) { border-color:#000; background:transparent; }
.library-footer { display:flex; align-items:center; flex-wrap:wrap; gap:8px; padding-top:16px; border-top:1px solid #e5e5e5; }
.library-footer span { color:#666; font-size:12px; }
.library-message { padding:8px 10px; font-size:13px; background:#f5f5f5; }
.settings-backdrop { position:fixed; inset:0; z-index:40; display:grid; place-items:center; padding:16px; background:#0005; }
.library-settings { width:min(360px,100%); max-height:calc(100dvh - 32px); overflow:auto; padding:20px; background:#fff; border:1px solid #000; border-radius:6px; }
.library-settings form,.library-settings label { display:grid; gap:6px; }
.library-settings form { gap:12px; }
.library-settings h2 { margin:0; font-size:18px; }
.library-settings .required-toggle { display:flex; align-items:center; }
.required-toggle input { accent-color:#000; }
.library-settings small { color:#666; font-size:12px; }
.settings-actions { display:flex; justify-content:flex-end; gap:8px; }
.sr-only { position:absolute; width:1px; height:1px; clip-path:inset(50%); overflow:hidden; }
@media(max-width:640px) { .library-page { padding:16px 12px 28px; }.library-heading { align-items:start; flex-direction:column; }.library-heading h1 { font-size:21px; }.library-create { gap:6px; }.library-create>label:first-child { flex-basis:100%; } }
</style>
